import { backoffice, BackofficeUser } from "@/endpoints/dryp/backoffice";
import { FormInput } from "@/components/forms/FormInput";
import { FormSubmitButton } from "@/components/forms/FormSubmitButton";
import { FormWrapper } from "@/components/forms/FormWrapper";
import { validation } from "@/domain/validation";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useFormHandler } from "@/hooks/useFormHandler";
import { useStore } from "@/hooks/useStore";
import { DialogHeader } from "@/components/ui/dialog";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { z } from "zod";

const schema = z
	.object({
		password: validation.passwordWithRules,
		confirmPassword: validation.passwordWithRules,
	})
	.refine(({ password, confirmPassword }) => password === confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

interface ChangePasswordProps {
	user: BackofficeUser;
	onSuccess: () => void;
}

export const ChangePassword: FC<ChangePasswordProps> = observer(({ user: targetUser, onSuccess }) => {
	const {
		userStore,
		uiStore: { modalStore },
	} = useStore();
	const isOwnUser = userStore.getUser?.name === targetUser.userName;

	const updatePasswordApi = useAsyncStateStore(backoffice.updateUserPassword);

	const { form, handleFormErrors } = useFormHandler(schema, {
		defaultValues: {
			password: "",
			confirmPassword: "",
		},
	});

	const onSubmit = async (data: z.infer<typeof schema>) => {
		try {
			await updatePasswordApi.fetch(targetUser.id, data.password);
			modalStore.close();
			onSuccess();
		} catch (error) {
			handleFormErrors(error);
		}
	};

	return (
		<>
			<DialogHeader>
				<DialogTitle>Change Password - {targetUser.userName}</DialogTitle>
				<DialogDescription className="sr-only">Change password for {targetUser.userName}.</DialogDescription>
			</DialogHeader>

			{isOwnUser ? (
				<p className="text-sm text-muted-foreground py-4">
					You cannot change your own password here. To reset your password, log out and use the "Forgot
					password" option on the login page.
				</p>
			) : (
				<FormWrapper form={form} onSubmit={onSubmit} error={updatePasswordApi.error}>
					<FormInput
						name="password"
						type="password"
						label="New Password"
						placeholder="Type new password"
						required
					/>
					<FormInput
						name="confirmPassword"
						type="password"
						label="Confirm Password"
						placeholder="Repeat new password"
						required
					/>
					<FormSubmitButton
						isLoading={updatePasswordApi.loading}
						buttonText="Change password"
						buttonLoadingText="Changing password"
					/>
				</FormWrapper>
			)}
		</>
	);
});
